import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Checkbox, Input, SaveBar, Select } from '../../components/shared';
import {
  getSubstitutePolicy, patchSubstitutePolicy,
  type SubstitutePolicyDto, type SubstituteExpireRule,
} from '../../api/hr-substitute';
import '../../styles/settings-screen.css';
import './Substitute.css';

/**
 * 휴일대체 › **정책** 탭 — 보상휴가(근기법 §57)를 회사가 켜는 자리 (2026-09-06 · 판 B).
 *
 * ★ 판 C 에서 **화면머리와 탭은 셸(`SubstitutePolicyPage`)이 갖는다** — 이 파일은 절과 저장바만.
 *
 * ## ★★★ 왜 필요했나
 *
 * 판 A 가 표도 API 도 만들었는데 **켜는 화면만 없었다** — 그래서 **어느 회사도 못 켰다.**
 * 연차정책이 겪은 것과 같은 자리다(「표가 있다 ≠ 쓸 수 있다」). 이 제품은 여러 회사가 쓴다.
 *
 * ## ★★★ 왜 기본이 꺼져 있나
 *
 * §57 은 **「근로자대표와의 서면 합의」** 로 여는 제도다 — 회사가 마음대로 「수당 대신 휴가」로
 * 바꿀 수 있는 것이 아니다. 그래서 스위치이고, 합의를 어디에 적어 뒀는지도 함께 남긴다.
 *
 * ## 정본 P2-1 설정 화면
 * 화면머리(`PageHeader`) · 절 · 줄(라벨 180px) · **아래 저장바**. 탭은 없다 — 절이 둘뿐이라
 * 가를 것이 없다(P2-1 은 「탭마다 다른 내용일 때」 탭을 세우라고 적는다).
 *
 * ★★ **저장바가 있는 화면이라 켬/끔은 `Checkbox` 다**(정본 B7 · P2-1) — `Switch` 는 즉시
 *    반영을 뜻한다. 한 화면은 한 가지 반영 시점만 갖는다.
 */

type FootState = null | 'saving' | 'saved' | 'failed' | 'load-failed';

const DEFAULTS: SubstitutePolicyDto = {
  enabled: false,
  allowOvertime: true, allowNight: true, allowHoliday: true,
  expireRule: 'YEAR_END_NEXT', expireMonths: 12, agreementNote: null,
};

const EXPIRE_OPTIONS = [
  { value: 'YEAR_END_NEXT', label: '일한 해의 다음 해 말까지' },
  { value: 'MONTHS', label: '일한 날부터 N개월' },
];

/** 무엇이 달라졌나 — 안 달라졌으면 저장바가 잠긴다(P2-1). */
export function substitutePolicyDirty(a: SubstitutePolicyDto, b: SubstitutePolicyDto): boolean {
  return a.enabled !== b.enabled
    || a.allowOvertime !== b.allowOvertime
    || a.allowNight !== b.allowNight
    || a.allowHoliday !== b.allowHoliday
    || a.expireRule !== b.expireRule
    || a.expireMonths !== b.expireMonths
    || (a.agreementNote ?? '') !== (b.agreementNote ?? '');
}

export function SubstitutePolicyForm(): JSX.Element {
  const [base, setBase] = useState<SubstitutePolicyDto>(DEFAULTS);
  const [draft, setDraft] = useState<SubstitutePolicyDto>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [foot, setFoot] = useState<FootState>(null);

  const uid = useId();
  const id = (k: string) => `${uid}-${k}`;

  useEffect(() => {
    let cancelled = false;
    getSubstitutePolicy()
      .then((p) => { if (!cancelled) { setBase(p); setDraft(p); } })
      .catch(() => { if (!cancelled) setFoot('load-failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const set = useCallback(<K extends keyof SubstitutePolicyDto>(k: K, v: SubstitutePolicyDto[K]) => {
    setDraft((prev) => ({ ...prev, [k]: v }));
    // 고치는 순간 「저장됨」을 거둔다 — 안 그러면 고친 뒤에도 저장됐다고 적혀 있다.
    setFoot((f) => (f === 'saved' ? null : f));
  }, []);

  const dirty = useMemo(() => substitutePolicyDirty(draft, base), [draft, base]);

  /**
   * ★★ **갈래를 다 끄면 아무것도 못 바꾼다** — 켜 놓고 후보가 늘 비는 상태가 된다.
   *    막지 않고 **말해 준다**(연차정책의 「법정 최소는 바닥이다」와 같은 판단).
   */
  const noKind = draft.enabled && !draft.allowOvertime && !draft.allowNight && !draft.allowHoliday;

  /**
   * ★★★ **근거 없이는 못 켠다** (판 1-10 · 2026-09-11 · §57).
   *
   *    갈래(`noKind`)와 달리 이쪽은 **서버가 400 으로 막는다.** 화면이 미리 말하지 않으면
   *    사용자는 「저장하지 못했습니다」만 보고 **왜인지 모른다** — 막는 것과 알려 주는 것은
   *    다른 일이다. 그래서 여기서는 말해 주고 **저장까지 잠근다**.
   * ★ 잣대는 서버와 **같다** — 공백만 적은 것은 근거가 아니다(`trim()`).
   * ★ **끄는 것은 잠기지 않는다** — `draft.enabled` 가 false 면 이 값도 false 다.
   *   끄지도 못하게 만들면 그게 더 나쁘다.
   */
  const noAgreement = draft.enabled && (draft.agreementNote ?? '').trim() === '';

  const save = useCallback(async () => {
    setFoot('saving');
    try {
      const saved = await patchSubstitutePolicy({
        enabled: draft.enabled,
        allowOvertime: draft.allowOvertime,
        allowNight: draft.allowNight,
        allowHoliday: draft.allowHoliday,
        expireRule: draft.expireRule,
        expireMonths: draft.expireMonths,
        // 빈 글자는 안 보낸다 — 서버가 «지운다»와 «안 바꾼다»를 가르지 않는다.
        ...(draft.agreementNote != null && draft.agreementNote.trim() !== ''
          ? { agreementNote: draft.agreementNote.trim() } : {}),
      });
      setBase(saved);
      setDraft(saved);
      setFoot('saved');
    } catch {
      setFoot('failed');
    }
  }, [draft]);

  const revert = useCallback(() => { setDraft(base); setFoot(null); }, [base]);

  return (
    <>
      {loading ? (
        <div className="wc-set__body"><p className="wc-set__hint">…</p></div>
      ) : (
        <div className="wc-set__body">
          {/* ══ 제도 ══ */}
          <section className="wc-set__sec" aria-labelledby={id('sec1')}>
            <h2 className="wc-set__sech" id={id('sec1')}>제도</h2>
            <div className="wc-set__rows">
              <div className="wc-set__row">
                <span className="wc-set__label">보상휴가제</span>
                <div className="wc-set__ctl">
                  <Checkbox
                    checked={draft.enabled}
                    onChange={(e) => set('enabled', e.target.checked)}
                    label="씁니다 — 직원이 「내 근태」에서 신청할 수 있습니다"
                  />
                  <p className="wc-set__hint">
                    근기법 §57 은 <b>근로자대표와의 서면 합의</b>가 있어야 쓸 수 있습니다.
                    켜기 전에 합의가 있어야 하고, 끄면 <b>새 신청만</b> 막힙니다 —
                    이미 쌓인 보상휴가는 그대로 남습니다.
                  </p>
                </div>
              </div>

              {/* ★ 딸린 칸 — 부모 줄 바로 아래에 세로선을 두고 선다(P2-1 「값에 딸린 칸」).
                  꺼져 있어도 **감추지 않는다**: 값은 살아 있고, 「켜면 이렇게 된다」를 미리 본다. */}
              <div className="wc-set__row wc-set__row--sub">
                <span className="wc-set__label" id={id('kinds')}>바꿀 수 있는 근로</span>
                <div className="wc-set__ctl">
                  <div className="wc-set__inline" role="group" aria-labelledby={id('kinds')}>
                    <Checkbox
                      checked={draft.allowOvertime} disabled={!draft.enabled}
                      onChange={(e) => set('allowOvertime', e.target.checked)} label="연장근로"
                    />
                    <Checkbox
                      checked={draft.allowNight} disabled={!draft.enabled}
                      onChange={(e) => set('allowNight', e.target.checked)} label="야간근로"
                    />
                    <Checkbox
                      checked={draft.allowHoliday} disabled={!draft.enabled}
                      onChange={(e) => set('allowHoliday', e.target.checked)} label="휴일근로"
                    />
                  </div>
                  {noKind && (
                    <p className="wc-set__bad" role="status">
                      셋을 다 끄면 바꿀 수 있는 근로가 하나도 없어, 켜 두어도 신청 화면이 늘 비어 있습니다.
                    </p>
                  )}
                  <p className="wc-set__hint">
                    §57 은 셋 다 허용합니다 — 회사가 좁힐 수 있습니다(예: 휴일근로만).
                    <b> 쌓이는 시간은 가산까지 넣은 값</b>입니다: 휴일근로 6시간 → 보상휴가 9시간.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══ 기한 ══ */}
          <section className="wc-set__sec" aria-labelledby={id('sec2')}>
            <h2 className="wc-set__sech" id={id('sec2')}>기한과 기록</h2>
            <div className="wc-set__rows">
              <div className="wc-set__row">
                <span className="wc-set__label">언제까지 쓰나</span>
                <div className="wc-set__ctl">
                  <Select
                    className="wc-set__w2"
                    ariaLabel="언제까지 쓰나"
                    value={draft.expireRule}
                    disabled={!draft.enabled}
                    onChange={(v) => set('expireRule', v as SubstituteExpireRule)}
                    options={EXPIRE_OPTIONS}
                  />
                  <p className="wc-set__hint">
                    기한은 <b>승인하는 순간 못 박습니다</b> — 나중에 이 값을 바꿔도
                    이미 준 휴가의 기한은 흔들리지 않습니다.
                    <b> 무기한은 두지 않습니다</b>(쓰지 않은 시간이 회사의 빚으로 쌓입니다).
                  </p>
                </div>
              </div>

              {draft.expireRule === 'MONTHS' && (
                <div className="wc-set__row wc-set__row--sub">
                  <span className="wc-set__label">몇 개월</span>
                  <div className="wc-set__ctl">
                    <Input
                      wrapperClassName="wc-set__w1"
                      type="number" min={1} max={60}
                      aria-label="몇 개월"
                      value={String(draft.expireMonths)}
                      disabled={!draft.enabled}
                      onChange={(e) => set('expireMonths', Math.max(1, Math.min(60, Number(e.target.value) || 1)))}
                      suffix="개월"
                    />
                  </div>
                </div>
              )}

              <div className="wc-set__row">
                <span className="wc-set__label">서면 합의</span>
                <div className="wc-set__ctl">
                  <Input
                    wrapperClassName="wc-set__w3"
                    aria-label="서면 합의"
                    value={draft.agreementNote ?? ''}
                    disabled={!draft.enabled}
                    maxLength={200}
                    onChange={(e) => set('agreementNote', e.target.value)}
                    placeholder="예) 2026-03-14 노사협의회 합의 (문서번호 HR-2026-004)"
                  />
                  {noAgreement && (
                    <p className="wc-set__bad" role="status">
                      보상휴가제는 근로자대표와의 서면 합의로만 도입할 수 있습니다(근로기준법 제57조).
                      합의 근거를 적어야 켤 수 있습니다.
                    </p>
                  )}
                  <p className="wc-set__hint">
                    합의를 어디에 적어 뒀는지 남깁니다 — 나중에 「무슨 근거로 켰나」를 되짚는 자리입니다.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ★ 「왜 그래야 하는가」는 도움말로(P8). 지금의 판정은 위 줄들이 이미 적었다. */}
          <p className="wc-set__hint">
            보상휴가로 돌린 시간은 <b>「연장 정산」의 예상 수당에서 빠집니다</b> —
            §57 이 「임금 지급을 <b>갈음</b>하여」이므로 휴가로 주고 수당도 주면 이중입니다.
            기한이 지나도록 쓰지 않은 몫은 <b>임금으로 돌려주어야</b> 합니다.
          </p>
        </div>
      )}

      {/* 저장 줄 한 벌(2026-10-18 · web 레인 W8) — 종전 `.wc-set__foot` 덩이의 글 · 조건을 그대로 SaveBar 에 넘긴다.
          ★ 판 1-10 — 근거 없이 켠 채로는 저장을 못 누른다(서버도 400 으로 막는다). */}
      <SaveBar
        canSave={dirty && !noAgreement}
        onSave={() => { void save(); }}
        saving={foot === 'saving'}
        onRevert={revert}
        canRevert={dirty && foot !== 'saving'}
        dirty={dirty}
        message={(foot === 'saving' ? '저장 중…'
          : foot === 'saved' ? '저장됨 · 방금'
            : foot === 'failed' ? '저장하지 못했습니다.'
              : foot === 'load-failed' ? '설정을 불러오지 못했습니다.'
                : '') || undefined}
        messageTone={foot === 'saved' ? 'ok' : foot === 'failed' || foot === 'load-failed' ? 'bad' : undefined}
        messageRole={foot === 'failed' ? 'alert' : 'status'}
        enableHint={dirty && noAgreement ? '서면 합의 근거를 적으면 켜집니다' : undefined}
      />
    </>
  );
}
